import React from 'react';
import { Tooltip } from '@mui/material';

/**
 * AiImportButton
 *
 * The dedicated entry point for the AI-Powered Unstructured Itinerary Importer:
 * a plus (+) glyph with a small AI spark/star overlayed on its top-left corner.
 *
 * Renders a self-contained <button>. Pass `onClick` to open the import modal.
 *
 * Props:
 *   onClick   () => void
 *   label     tooltip / aria-label (default provided)
 *   size      pixel size of the icon box (default 22)
 *   variant   'icon' (default) | 'pill'  — pill shows a text label too
 *   pillText  label text for the pill variant
 */
export default function AiImportButton({ onClick, label = 'Import itinerary with AI', size = 22, variant = 'icon', pillText = 'AI Import' }) {
    const glyph = (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            {/* Plus sign */}
            <path d="M12 7.5V18.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M6.5 13H17.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            {/* AI spark / 4-point star in the top-left corner */}
            <path
                d="M5 1.4 L5.9 3.6 L8.1 4.5 L5.9 5.4 L5 7.6 L4.1 5.4 L1.9 4.5 L4.1 3.6 Z"
                fill="#a855f7"
                stroke="#c084fc"
                strokeWidth="0.4"
                strokeLinejoin="round"
            />
        </svg>
    );

    if (variant === 'pill') {
        return (
            <Tooltip title={label}>
                <button
                    type="button"
                    onClick={onClick}
                    aria-label={label}
                    className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-2xl tw-font-semibold tw-text-white tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02] tw-shadow-lg"
                    style={{ background: 'linear-gradient(135deg,#6C63FF,#a855f7)' }}
                >
                    <span style={{ display: 'inline-flex', color: '#fff' }}>{glyph}</span>
                    {pillText}
                </button>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={label}>
            <button
                type="button"
                onClick={onClick}
                aria-label={label}
                className="tw-inline-flex tw-items-center tw-justify-center tw-p-2 tw-rounded-2xl tw-text-white/85 tw-transition-all tw-duration-200 hover:tw-text-white hover:tw-bg-white/10 tw-border tw-border-transparent hover:tw-border-white/10"
            >
                {glyph}
            </button>
        </Tooltip>
    );
}
